import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import {
  Loader2, FileText, Mail, Eye, CheckCircle2, CreditCard, BadgeCheck, XCircle,
  Clock, Ban, CalendarX, Activity, ShieldQuestion, Bot, Copy, RotateCcw,
} from "lucide-react";

const STAGE_FLOW = ["DRAFT", "SHARED", "VIEWED", "ACCEPTED_AWAITING_PAYMENT", "PAID"];
const STAGE_SHORT = {
  DRAFT: "Draft", SHARED: "Shared", VIEWED: "Viewed",
  ACCEPTED_AWAITING_PAYMENT: "Accepted", PAID: "Paid",
};

const EVENT_META = {
  PROPOSAL_CREATED: { icon: FileText, label: "Proposal created", tone: "text-slate-600 bg-slate-100" },
  PROPOSAL_SENT: { icon: Mail, label: "Shared with client", tone: "text-indigo-700 bg-indigo-100" },
  PROPOSAL_VIEWED: { icon: Eye, label: "Viewed by a person", tone: "text-violet-700 bg-violet-100" },
  PROPOSAL_ACCEPTED: { icon: CheckCircle2, label: "Proposal accepted", tone: "text-emerald-700 bg-emerald-100" },
  CHECKOUT_INITIATED: { icon: CreditCard, label: "Checkout started", tone: "text-amber-700 bg-amber-100" },
  PAYMENT_CONFIRMED: { icon: BadgeCheck, label: "Payment confirmed", tone: "text-emerald-700 bg-emerald-100" },
  PAYMENT_FAILED: { icon: XCircle, label: "Payment failed", tone: "text-red-700 bg-red-100" },
  PAYMENT_EXPIRED: { icon: Clock, label: "Checkout expired", tone: "text-stone-600 bg-stone-100" },
  PROPOSAL_DECLINED: { icon: Ban, label: "Proposal declined", tone: "text-red-700 bg-red-100" },
  PROPOSAL_EXPIRED: { icon: CalendarX, label: "Proposal expired", tone: "text-amber-700 bg-amber-100" },
  PAYMENT_REFUNDED: { icon: RotateCcw, label: "Payment refunded", tone: "text-orange-700 bg-orange-100" },
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function eventDetail(e, cur) {
  if (e.type === "PROPOSAL_SENT" && e.recipient) return `To ${e.recipient}`;
  if (e.type === "PROPOSAL_ACCEPTED") return [e.name, e.amount ? fmtMoney(e.amount, e.currency || cur) : null].filter(Boolean).join(" · ");
  if (e.type === "PROPOSAL_VIEWED") return e.ua_family || "Browser";
  if (["CHECKOUT_INITIATED", "PAYMENT_CONFIRMED", "PAYMENT_FAILED", "PAYMENT_EXPIRED", "PAYMENT_REFUNDED"].includes(e.type))
    return [e.label, e.amount ? fmtMoney(e.amount, e.currency || cur) : null].filter(Boolean).join(" · ");
  return "";
}

export default function ActivityTimeline({ id }) {
  const [data, setData] = useState(undefined); // undefined=loading, null=error

  useEffect(() => {
    let alive = true;
    api.get(`/proposals/${id}/activity`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [id]);

  if (data === undefined)
    return (
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 grid place-items-center" data-testid="activity-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
      </div>
    );
  if (data === null) return null;

  const cur = data.amounts?.currency || "USD";
  const vs = data.view_summary || {};
  const activeIdx = STAGE_FLOW.indexOf(data.stage);
  const terminal = ["EXPIRED", "CANCELLED", "REFUNDED"].includes(data.stage);

  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="activity-timeline">
      <div className="flex items-center gap-2 mb-5">
        <span className="grid place-items-center w-8 h-8 rounded-lg bg-slate-900 text-white"><Activity className="w-4 h-4" /></span>
        <div>
          <h3 className="font-heading font-bold text-slate-900">Activity & sales pipeline</h3>
          <p className="text-xs text-slate-500">Internal — never shown to the client.</p>
        </div>
      </div>

      {/* Pipeline stepper */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="pipeline-stepper">
        {STAGE_FLOW.map((s, i) => {
          const done = !terminal && activeIdx >= i;
          const current = !terminal && activeIdx === i;
          return (
            <div key={s} className="flex items-center gap-1.5 shrink-0">
              <div data-testid={`stage-${s}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                  current ? "bg-slate-900 text-white border-slate-900"
                  : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white text-slate-400 border-slate-200"}`}>
                {STAGE_SHORT[s]}
              </div>
              {i < STAGE_FLOW.length - 1 && <span className={`w-5 h-px ${done ? "bg-emerald-300" : "bg-slate-200"}`} />}
            </div>
          );
        })}
        {terminal && (
          <span data-testid="stage-terminal" className={`ml-2 rounded-full px-3 py-1 text-xs font-semibold border shrink-0 ${data.stage === "REFUNDED" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-red-50 text-red-700 border-red-200"}`}>
            {data.stage_label}
          </span>
        )}
      </div>

      {/* Amount summary */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm" data-testid="activity-amounts">
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Proposal value</div>
          <div className="font-bold text-slate-900">{fmtMoney(data.amounts?.one_time_client_price, cur)}</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Accepted</div>
          <div className="font-bold text-slate-900">{data.amounts?.accepted_price ? fmtMoney(data.amounts.accepted_price, cur) : "—"}</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Paid (verified)</div>
          <div className={`font-bold ${data.paid ? "text-emerald-700" : "text-slate-400"}`} data-testid="activity-paid">
            {data.paid ? fmtMoney(data.amounts?.paid_amount ?? data.amounts?.one_time_client_price, cur) : "Not yet"}
          </div>
        </div>
      </div>

      {/* View engagement */}
      <div className="mt-4 rounded-xl border border-slate-200 p-4" data-testid="view-summary">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
            <Eye className="w-4 h-4 text-violet-500" /> {vs.human_views || 0} likely human view{(vs.human_views || 0) === 1 ? "" : "s"}
          </span>
          {vs.last_human_view_at && <span className="text-xs text-slate-500">Last: {fmtTime(vs.last_human_view_at)}</span>}
        </div>
        {(vs.automated_or_bot || vs.uncertain || vs.duplicate) > 0 && (
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500" data-testid="view-noise">
            {vs.automated_or_bot > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5"><Bot className="w-3 h-3" /> {vs.automated_or_bot} automated / bot</span>}
            {vs.uncertain > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5"><ShieldQuestion className="w-3 h-3" /> {vs.uncertain} uncertain</span>}
            {vs.duplicate > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5"><Copy className="w-3 h-3" /> {vs.duplicate} duplicate refresh</span>}
          </div>
        )}
        <details className="mt-2" data-testid="view-disclaimer">
          <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600">How is viewing measured?</summary>
          <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{vs.disclaimer}</p>
        </details>
      </div>

      {/* Chronological timeline */}
      <div className="mt-5" data-testid="event-timeline">
        {(data.timeline || []).length === 0 ? (
          <p className="text-sm text-slate-400">No activity recorded yet.</p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-3 space-y-4">
            {data.timeline.map((e, i) => {
              const meta = EVENT_META[e.type] || { icon: Activity, label: e.type, tone: "text-slate-600 bg-slate-100" };
              const Icon = meta.icon;
              const detail = eventDetail(e, cur);
              return (
                <li key={e.public_id || i} className="ml-5" data-testid={`event-${e.type}`}>
                  <span className={`absolute -left-[13px] grid place-items-center w-6 h-6 rounded-full ring-4 ring-white ${meta.tone}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <p className="font-medium text-slate-900 text-sm">{meta.label}{detail ? <span className="font-normal text-slate-500"> — {detail}</span> : null}</p>
                    <time className="text-xs text-slate-400">{fmtTime(e.at)}</time>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
